// ============================================================
// Citation Calm™ — Starter / Full Defense / Facility
// Route: /for-directors  (URL preserved — no 301 needed)
// Renamed from: DaycareDirector.tsx
// V 10-Section Template: Name → Verdict → Who → What → Get → How →
//   System Context → Not For You If → Proof → CTA
// ISO: 2026-05-06
// ============================================================

import { Link } from "react-router-dom";

const SE = {
  cream:  "var(--se-cream, #f9f6f1)",
  black:  "var(--se-black, #1a1a1a)",
  gold:   "var(--se-gold, #d6b168)",
  soft:   "var(--se-soft-gray, #e6e2dc)",
  mid:    "var(--se-mid-gray, #b8b3ac)",
  deep:   "var(--se-deep-charcoal, #2a2a2a)",
  serif:  "var(--font-serif, 'Cormorant Garamond', Georgia, serif)",
  sans:   "var(--font-sans, 'Inter', system-ui, sans-serif)",
  mono:   "var(--font-mono, 'IBM Plex Mono', 'DM Mono', monospace)",
  prof:   "var(--accent-professional, #5c6b7a)",
};

const TIERS = [
  {
    key:    "citation_calm_starter",
    tier:   "DIRECTOR STARTER",
    name:   "Citation Calm™ — Director Starter",
    price:  "$97/yr",
    desc:   "Plain-language inspection breakdown for new directors. 20 analyses per month. You received a citation. This tells you what it actually says before you respond.",
    items:  [
      "20 citation analyses per month",
      "Plain-language breakdown of regulatory language",
      "Obligation vs recommendation detection",
      "Timeline and deadline extraction",
      "Exportable analysis for documentation",
    ],
    cta:     "Activate Director Starter",
    stripeKey: "citation_calm_starter",
  },
  {
    key:    "citation_calm_full",
    tier:   "FULL DEFENSE",
    name:   "Citation Calm™ — Full Defense",
    price:  "$197/yr",
    desc:   "Unlimited inspection analysis plus Admin Review document generation. For directors managing active citations or preparing formal appeals.",
    items:  [
      "Unlimited citation analyses",
      "Admin Review document generation",
      "Formal appeal drafting assistance",
      "Citation pattern history",
      "Exportable evidence-ready reports",
      "Priority analysis for urgent citations",
    ],
    cta:     "Activate Full Defense",
    stripeKey: "citation_calm_full",
    featured: true,
  },
  {
    key:    "citation_calm_facility",
    tier:   "FACILITY",
    name:   "Citation Calm™ — Facility",
    price:  "$497/yr",
    desc:   "Up to 5 staff members. Full Defense for your entire facility team — directors, assistant directors, and compliance staff.",
    items:  [
      "Up to 5 staff seats",
      "All Full Defense features for each seat",
      "Shared admin dashboard",
      "Team analysis history",
      "Coordinated citation response",
    ],
    cta:     "Activate Facility Plan",
    stripeKey: "citation_calm_facility",
  },
];

export default function CitationCalm() {
  return (
    <div style={{ background: SE.cream, fontFamily: SE.sans, color: SE.black }}>

      {/* ── NAV ──────────────────────────────────────────────────────── */}
      <nav style={{
        background: SE.black, padding: "0 32px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        height: "52px", borderBottom: `1px solid ${SE.deep}`,
      }}>
        <Link to="/" style={{
          fontFamily: SE.mono, fontSize: "0.65rem", letterSpacing: "0.15em",
          color: SE.gold, textDecoration: "none", textTransform: "uppercase",
        }}>
          ← Catch The Pattern™
        </Link>
        <span style={{
          fontFamily: SE.mono, fontSize: "0.65rem", letterSpacing: "0.14em",
          color: SE.mid, textTransform: "uppercase",
        }}>
          Citation Calm™ — For Directors
        </span>
        <Link to="/inspection-defense" style={{
          fontFamily: SE.sans, fontSize: "0.78rem", color: SE.mid,
          textDecoration: "none", letterSpacing: "0.03em",
        }}>
          Inspection Defense™ →
        </Link>
      </nav>

      {/* ── §1: PRODUCT NAME + VERDICT ───────────────────────────────── */}
      <section style={{
        background: SE.black, color: SE.cream,
        padding: "88px 32px 72px", textAlign: "center",
      }}>
        <p style={{
          fontFamily: SE.mono, fontSize: "0.6rem", letterSpacing: "0.2em",
          color: SE.gold, textTransform: "uppercase", marginBottom: "16px",
        }}>
          AN ELLARI VENTURES SYSTEM — CARETIDE VERTICAL
        </p>
        <h1 style={{
          fontFamily: SE.serif, fontSize: "clamp(2.4rem, 5vw, 3.8rem)",
          fontWeight: 300, maxWidth: "760px", margin: "0 auto 20px", lineHeight: 1.15,
        }}>
          Citation Calm™
        </h1>
        {/* VERDICT */}
        <p style={{
          fontFamily: SE.mono, fontSize: "0.75rem", color: SE.gold,
          letterSpacing: "0.12em", marginBottom: "20px", textTransform: "uppercase",
        }}>
          YOU RECEIVED A CITATION. THIS TELLS YOU WHAT IT ACTUALLY SAYS BEFORE YOU RESPOND.
        </p>
        <p style={{
          color: SE.mid, fontSize: "1rem", maxWidth: "580px",
          margin: "0 auto 40px", lineHeight: 1.75,
        }}>
          Inspection language is designed for regulatory authority, not director comprehension.
          Citation Calm™ translates every finding into plain language — obligations, timelines,
          appeal windows, and what the inspector actually documented.
        </p>
        <div style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
          <a href="#plans" style={{
            background: SE.gold, color: SE.black,
            padding: "14px 36px", fontFamily: SE.sans, fontWeight: 700,
            fontSize: "0.875rem", textDecoration: "none",
            borderRadius: "2px", letterSpacing: "0.05em",
          }}>
            Choose A Plan →
          </a>
          <a href="#what-it-does" style={{
            border: `1px solid ${SE.mid}`, color: SE.cream,
            padding: "14px 36px", fontFamily: SE.sans, fontSize: "0.875rem",
            textDecoration: "none", borderRadius: "2px", letterSpacing: "0.04em",
          }}>
            See How It Works
          </a>
        </div>
      </section>

      <Divider />

      {/* ── §2: WHO IT IS BUILT FOR ──────────────────────────────────── */}
      <section style={{ padding: "64px 32px", maxWidth: "900px", margin: "0 auto" }}>
        <Label label="WHO IT IS BUILT FOR" />
        <h2 style={{ fontFamily: SE.serif, fontSize: "clamp(1.4rem, 2.8vw, 2rem)", fontWeight: 400, margin: "16px 0 24px" }}>
          Licensed childcare directors navigating real regulatory pressure.
        </h2>
        <p style={{ fontSize: "0.92rem", color: SE.mid, lineHeight: 1.75, maxWidth: "700px" }}>
          Citation Calm™ was built by a licensed Texas childcare operator with four active
          facilities. It was built to answer the question that appears after every inspection:
          "What does this actually require me to do, and by when?"
        </p>
        <div style={{
          marginTop: "32px", display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
          gap: "16px",
        }}>
          {[
            "Licensed childcare directors (center-based)",
            "Licensed home childcare operators",
            "Assistant directors managing compliance paperwork",
            "Program administrators overseeing multiple sites",
            "Directors preparing formal appeal responses",
          ].map((who, i) => (
            <div key={i} style={{
              padding: "16px 18px", background: "#fff",
              border: `1px solid ${SE.soft}`, borderRadius: "4px",
              fontSize: "0.83rem", lineHeight: 1.5,
            }}>
              <span style={{ fontFamily: SE.mono, color: SE.gold, marginRight: "8px" }}>◆</span>
              {who}
            </div>
          ))}
        </div>
      </section>

      <Divider />

      {/* ── §3: WHAT IT DOES ─────────────────────────────────────────── */}
      <section id="what-it-does" style={{ background: "#fff", padding: "64px 32px" }}>
        <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
          <Label label="WHAT IT DOES" />
          <h2 style={{ fontFamily: SE.serif, fontSize: "clamp(1.4rem, 2.8vw, 2rem)", fontWeight: 400, margin: "16px 0 36px" }}>
            Every citation contains three layers. Most directors only see one.
          </h2>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
            gap: "20px",
          }}>
            {[
              {
                step: "01",
                title: "Regulatory Language Translation",
                body: "Converts inspector citation language from Texas HHSC / licensing standard terminology into plain-language plain English. What the citation says in formal code, Citation Calm™ says in what it means for your license.",
              },
              {
                step: "02",
                title: "Obligation vs. Recommendation Detection",
                body: "Identifies whether each cited item is a mandatory corrective action, an advisory recommendation, or a documentation requirement. These have different response obligations and timelines.",
              },
              {
                step: "03",
                title: "Timeline and Deadline Extraction",
                body: "Extracts every deadline embedded in the citation — correction deadlines, appeal windows, re-inspection scheduling requirements, and compliance confirmation dates.",
              },
              {
                step: "04",
                title: "Admin Review Document Generation",
                body: "Full Defense and Facility plans generate Admin Review response documents — the formal director-level response to citations that goes back to licensing staff. Evidence-ready format.",
              },
            ].map(card => (
              <div key={card.step} style={{
                padding: "24px", border: `1px solid ${SE.soft}`,
                borderTop: `3px solid ${SE.gold}`, borderRadius: "4px",
              }}>
                <div style={{
                  fontFamily: SE.mono, fontSize: "0.6rem",
                  color: SE.gold, marginBottom: "10px", letterSpacing: "0.14em",
                }}>
                  {card.step}
                </div>
                <div style={{ fontFamily: SE.serif, fontSize: "1rem", marginBottom: "10px" }}>
                  {card.title}
                </div>
                <div style={{ fontSize: "0.8rem", color: SE.mid, lineHeight: 1.65 }}>
                  {card.body}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Divider />

      {/* ── §4: PLANS ────────────────────────────────────────────────── */}
      <section id="plans" style={{ padding: "72px 32px", maxWidth: "1200px", margin: "0 auto" }}>
        <Label label="PLANS" />
        <h2 style={{ fontFamily: SE.serif, fontSize: "clamp(1.4rem, 2.8vw, 2rem)", fontWeight: 400, margin: "16px 0 8px" }}>
          Choose the plan that matches your current regulatory exposure.
        </h2>
        <p style={{ color: SE.mid, fontSize: "0.88rem", marginBottom: "44px", maxWidth: "560px" }}>
          Start with Director Starter if you are new to the citation response process.
          Upgrade to Full Defense if you have active citations or are preparing an appeal.
        </p>
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
          gap: "20px",
        }}>
          {TIERS.map(tier => (
            <div key={tier.key} style={{
              background: tier.featured ? SE.black : "#fff",
              border: `2px solid ${tier.featured ? SE.gold : SE.soft}`,
              borderRadius: "4px", padding: "32px",
              position: "relative",
            }}>
              {tier.featured && (
                <div style={{
                  position: "absolute", top: "16px", right: "16px",
                  background: SE.gold, color: SE.black,
                  fontFamily: SE.mono, fontSize: "0.56rem",
                  letterSpacing: "0.12em", padding: "4px 10px", textTransform: "uppercase",
                }}>
                  RECOMMENDED
                </div>
              )}
              <div style={{
                fontFamily: SE.mono, fontSize: "0.58rem", color: SE.gold,
                letterSpacing: "0.16em", marginBottom: "10px", textTransform: "uppercase",
              }}>
                {tier.tier}
              </div>
              <div style={{
                fontFamily: SE.serif, fontSize: "1.1rem",
                color: tier.featured ? SE.cream : SE.black, marginBottom: "4px",
              }}>
                {tier.name}
              </div>
              <div style={{
                fontFamily: SE.mono, fontSize: "1.1rem",
                color: tier.featured ? SE.cream : SE.black, marginBottom: "12px",
              }}>
                {tier.price}
              </div>
              <p style={{
                fontSize: "0.82rem", lineHeight: 1.65,
                color: SE.mid, marginBottom: "24px",
              }}>
                {tier.desc}
              </p>
              <ul style={{ listStyle: "none", padding: 0, margin: "0 0 28px", display: "flex", flexDirection: "column", gap: "8px" }}>
                {tier.items.map((item, i) => (
                  <li key={i} style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
                    <span style={{ fontFamily: SE.mono, color: SE.gold, fontSize: "0.68rem", marginTop: "2px", flexShrink: 0 }}>◆</span>
                    <span style={{ fontSize: "0.79rem", color: SE.mid, lineHeight: 1.5 }}>{item}</span>
                  </li>
                ))}
              </ul>
              <a
                href={`https://shopellari.com/product/${tier.stripeKey}`}
                style={{
                  display: "block", textAlign: "center",
                  background: tier.featured ? SE.gold : SE.black,
                  color: tier.featured ? SE.black : SE.cream,
                  padding: "13px 20px", fontFamily: SE.sans, fontWeight: 700,
                  fontSize: "0.83rem", textDecoration: "none",
                  borderRadius: "2px", letterSpacing: "0.04em",
                }}
              >
                {tier.cta}
              </a>
            </div>
          ))}
        </div>
      </section>

      <Divider />

      {/* ── §5: HOW TO USE IT ────────────────────────────────────────── */}
      <section style={{ background: "#fff", padding: "64px 32px" }}>
        <div style={{ maxWidth: "760px", margin: "0 auto" }}>
          <Label label="HOW TO USE IT" />
          <h2 style={{ fontFamily: SE.serif, fontSize: "clamp(1.4rem, 2.8vw, 2rem)", fontWeight: 400, margin: "16px 0 32px" }}>
            From citation received to documented response.
          </h2>
          <div style={{ display: "flex", flexDirection: "column", gap: "0" }}>
            {[
              { n: "01", step: "Paste the citation text", body: "Copy the finding directly from the HHSC inspection report. No formatting required." },
              { n: "02", step: "Review the analysis",     body: "Citation Calm™ returns the plain-language breakdown, obligation classification, and deadline extraction within seconds." },
              { n: "03", step: "Generate the response",   body: "Full Defense and Facility plans generate an Admin Review document in evidence-ready format. Export or copy directly." },
              { n: "04", step: "Document the record",     body: "All analyses are stored in your account history. Every entry is timestamped and exportable for compliance documentation." },
            ].map(item => (
              <div key={item.n} style={{
                display: "flex", gap: "24px", alignItems: "flex-start",
                padding: "20px 0", borderBottom: `1px solid ${SE.soft}`,
              }}>
                <div style={{
                  fontFamily: SE.mono, fontSize: "0.65rem", color: SE.gold,
                  letterSpacing: "0.12em", minWidth: "28px", marginTop: "4px",
                }}>
                  {item.n}
                </div>
                <div>
                  <div style={{ fontFamily: SE.serif, fontSize: "1rem", marginBottom: "6px" }}>
                    {item.step}
                  </div>
                  <div style={{ fontSize: "0.82rem", color: SE.mid, lineHeight: 1.6 }}>
                    {item.body}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Divider />

      {/* ── §6: SYSTEM CONTEXT ───────────────────────────────────────── */}
      <section style={{ padding: "60px 32px", maxWidth: "900px", margin: "0 auto" }}>
        <Label label="SYSTEM CONTEXT" />
        <h2 style={{ fontFamily: SE.serif, fontSize: "clamp(1.3rem, 2.5vw, 1.8rem)", fontWeight: 400, margin: "16px 0 20px" }}>
          Citation Calm™ belongs to the Inspection Defense™ system.
        </h2>
        <p style={{ fontSize: "0.88rem", color: SE.mid, lineHeight: 1.75, maxWidth: "680px", marginBottom: "24px" }}>
          Citation Calm™ is director-facing: plain-language translation and response generation.
          Inspection Defense™ is operator-facing: structural analysis of regulatory language and
          Admin Review generation for the full licensed operator environment.
          They are designed to be used together.
        </p>
        <div style={{
          display: "flex", gap: "12px", flexWrap: "wrap",
        }}>
          <Link to="/inspection-defense" style={{
            background: SE.black, color: SE.cream,
            padding: "11px 24px", fontFamily: SE.sans, fontWeight: 600,
            fontSize: "0.82rem", textDecoration: "none",
            borderRadius: "2px", letterSpacing: "0.04em",
          }}>
            See Inspection Defense™ →
          </Link>
          <a href="https://shopellari.com/stacks/record-it-stack" style={{
            border: `1px solid ${SE.soft}`, color: SE.mid,
            padding: "11px 24px", fontFamily: SE.sans, fontSize: "0.82rem",
            textDecoration: "none", borderRadius: "2px", letterSpacing: "0.04em",
          }}>
            The Record It Stack™ →
          </a>
        </div>
      </section>

      <Divider />

      {/* ── §7: NOT FOR YOU IF ───────────────────────────────────────── */}
      <section style={{ padding: "60px 32px", maxWidth: "760px", margin: "0 auto" }}>
        <Label label="NOT FOR YOU IF" />
        <div style={{ marginTop: "20px", display: "flex", flexDirection: "column", gap: "10px" }}>
          {[
            "You are not a licensed childcare director or program administrator",
            "Your citations are not from a state or county licensing authority",
            "You want real-time legal advice — Citation Calm™ is a language analysis tool, not legal counsel",
            "You need a pattern recognition training environment — see Catch The Pattern™ instead",
            "You only need one citation analyzed — the Director Starter plan covers 20 per month at $97/yr",
          ].map((item, i) => (
            <div key={i} style={{
              display: "flex", gap: "12px", padding: "14px 16px",
              background: "#fff", border: `1px solid ${SE.soft}`,
              borderRadius: "4px",
            }}>
              <span style={{
                fontFamily: SE.mono, color: SE.mid,
                fontSize: "0.75rem", marginTop: "2px", flexShrink: 0,
              }}>×</span>
              <span style={{ fontSize: "0.83rem", color: SE.deep, lineHeight: 1.55 }}>{item}</span>
            </div>
          ))}
        </div>
      </section>

      <Divider />

      {/* ── §8: PROOF ────────────────────────────────────────────────── */}
      <section style={{ background: SE.black, color: SE.cream, padding: "60px 32px" }}>
        <div style={{ maxWidth: "760px", margin: "0 auto" }}>
          <Label label="PROOF" dark />
          <h2 style={{ fontFamily: SE.serif, fontSize: "clamp(1.3rem, 2.5vw, 1.8rem)", fontWeight: 400, margin: "16px 0 20px" }}>
            Built inside a licensed operation. Used on real citations.
          </h2>
          <p style={{ color: SE.mid, lineHeight: 1.75, marginBottom: "24px", fontSize: "0.88rem" }}>
            Citation Calm™ was developed by a licensed Texas childcare operator with four active
            facilities under HHSC oversight. The analysis engine was trained and refined on actual
            Texas minimum standards citations — not hypothetical examples.
          </p>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
            gap: "16px", marginTop: "24px",
          }}>
            {[
              { stat: "4",     label: "Active licensed facilities" },
              { stat: "TX",    label: "HHSC minimum standards" },
              { stat: "197",   label: "Annual license fee — Full Defense" },
              { stat: "ELLARI", label: "Verified system artifact" },
            ].map(item => (
              <div key={item.stat} style={{
                padding: "20px 16px",
                border: `1px solid ${SE.deep}`, borderRadius: "4px",
              }}>
                <div style={{ fontFamily: SE.mono, fontSize: "1.3rem", color: SE.gold, marginBottom: "6px" }}>
                  {item.stat}
                </div>
                <div style={{ fontSize: "0.75rem", color: SE.mid }}>{item.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── §9: FINAL CTA ────────────────────────────────────────────── */}
      <section style={{ padding: "80px 32px", textAlign: "center", background: SE.cream }}>
        <p style={{
          fontFamily: SE.mono, fontSize: "0.62rem", letterSpacing: "0.18em",
          color: SE.gold, marginBottom: "16px", textTransform: "uppercase",
        }}>
          BEGIN
        </p>
        <h2 style={{
          fontFamily: SE.serif, fontSize: "clamp(1.8rem, 4vw, 2.6rem)",
          fontWeight: 400, maxWidth: "560px", margin: "0 auto 12px",
        }}>
          You received a citation. Now you'll know what it actually says.
        </h2>
        <p style={{ color: SE.mid, fontSize: "0.88rem", marginBottom: "32px" }}>
          Starting at $97/year. Cancel anytime.
        </p>
        <a href="#plans" style={{
          background: SE.black, color: SE.cream,
          padding: "16px 48px", fontFamily: SE.sans, fontWeight: 700,
          fontSize: "0.875rem", textDecoration: "none",
          borderRadius: "2px", letterSpacing: "0.06em",
          display: "inline-block",
        }}>
          Choose A Plan →
        </a>
      </section>

      {/* ── FOOTER ───────────────────────────────────────────────────── */}
      <footer style={{
        background: SE.deep, padding: "24px 32px",
        display: "flex", justifyContent: "space-between",
        alignItems: "center", flexWrap: "wrap", gap: "12px",
      }}>
        <span style={{ fontFamily: SE.mono, fontSize: "0.6rem", color: SE.mid, letterSpacing: "0.1em" }}>
          CITATION CALM™ — AN ELLARI VENTURES SYSTEM
        </span>
        <div style={{ display: "flex", gap: "20px" }}>
          {[
            { label: "Inspection Defense™", href: "/inspection-defense" },
            { label: "Catch The Pattern™",  href: "/" },
            { label: "ShopEllari",          href: "https://shopellari.com" },
          ].map(link => (
            <a key={link.label} href={link.href} style={{
              fontFamily: SE.sans, fontSize: "0.73rem",
              color: SE.mid, textDecoration: "none",
            }}>
              {link.label}
            </a>
          ))}
        </div>
      </footer>

    </div>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────────────
function Label({ label, dark }: { label: string; dark?: boolean }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
      <div style={{ height: "1px", width: "20px", background: "var(--se-gold, #d6b168)" }} />
      <span style={{
        fontFamily: "var(--font-mono, monospace)", fontSize: "0.6rem",
        letterSpacing: "0.18em", color: "var(--se-gold, #d6b168)",
        textTransform: "uppercase",
      }}>
        {label}
      </span>
    </div>
  );
}

function Divider() {
  return <div style={{ height: "1px", background: "var(--se-gold, #d6b168)", opacity: 0.2, margin: "0 32px" }} />;
}
